import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';

import MosCheckbox from '@mos-front-components/public/es/components/MosCheckbox';
import Message from '@mos-front-components/public/es/components/Message';

import { agree, getExamData } from '../../store/examData/actions';
import { changeGiaItem, blurGiaItem } from '../../store/gia/actions';
import { saveDraft } from '../../store/draft/actions';

import {
  giaSelectedDefaultProp,
  giaSelectedPropTypes,
  giaBlurPropTypes
} from '../../propTypes/giaPropTypes';

import structuredSelector from './structuredSelector';
import GiaCodeSelector from './GiaCodeSelector';
import SubjectsInfo from './SubjectsInfo';
import Button from '../../components/Button/Button';

import './StepGia.pcss';
import { sendCMAAppealBusinessError } from '../../helpers/CMAStats';

const { TYPE } = Message;

const StepGia = ({
  getExamData,
  agreementOn,
  invalidExamAttributes,
  registrationCodes,
  documentNumbers,
  subjectsInfo,
  loadingExamData,
  changeGiaItem,
  blurGiaItem,
  giaSelected: { document_number, registration_code },
  giaSelected,
  giaBlur,
  agree,
  hasAvailableExams,
  appealResultNotReady,
  saveDraft
}) => {
  const requestExamData = ev => {
    ev.preventDefault();

    if (
      !invalidExamAttributes &&
      giaSelected.registration_code.valid &&
      giaSelected.document_number.valid
    ) {
      getExamData({
        giaNumber: document_number.value,
        giaCode: registration_code.value
      });
    }
  };

  const buttonHandler = ev => {
    requestExamData(ev);
    blurGiaItem(['document_number', 'registration_code']);
  };

  if (!registrationCodes && !documentNumbers) {
    return null;
  }

  useEffect(() => {
    if (subjectsInfo.length && !hasAvailableExams) {
      sendCMAAppealBusinessError({ stage: 'step' });
    }
  }, [hasAvailableExams, subjectsInfo]);

  useEffect(() => {
    if (invalidExamAttributes || appealResultNotReady) {
      sendCMAAppealBusinessError({ stage: 'step' });
    }
  }, [invalidExamAttributes, appealResultNotReady]);

  return (
    <form className="StepGia">
      <div className="StepGia__GIA">
        <GiaCodeSelector
          giaSelected={giaSelected}
          registrationCodes={registrationCodes}
          documentNumbers={documentNumbers}
          changeGiaItem={changeGiaItem}
          blurGiaItem={blurGiaItem}
          hasError={invalidExamAttributes}
          blur={giaBlur}
          saveDraft={saveDraft}
        />

        {invalidExamAttributes && (
          <div className="InvalidMessage">
            <Message type={TYPE.ERROR}>
              Указанные вами сведения об участнике ГИА не найдены. Проверьте,
              пожалуйста, указанные сведения и при необходимости скорректируйте
              данные.
              <br />
              Обращаем внимание, что подача апелляции о несогласии с
              выставленными баллами возможна только при наличии результатов ГИА
              и в течение 2-х рабочих дней, следующих за официальным днем
              объявления результатов экзамена по соответствующему учебному
              предмету.
            </Message>
          </div>
        )}

        {appealResultNotReady && (
          <div className="InvalidMessage">
            <Message
              className="InvalidMessage__ResultNotReady"
              type={TYPE.WARNING}
              smallerText
              bordered
            >
              <p>
                В настоящее время подача апелляций о несогласии с выставленными
                баллами по выбранным вами экзаменам недоступна.
              </p>
              <p>
                Ознакомиться с ПРИМЕРНЫМ графиком подачи и рассмотрения
                апелляций о несогласии с выставленными баллами в городе Москве
                вы можете на сайте регионального центра обработки информации
                города Москвы.
              </p>
              <p>
                ГИА-9:{' '}
                <a
                  className="Link"
                  rel="noopener noreferrer"
                  target="_blank"
                  href="http://rcoi.mcko.ru/gia-9-oge-gve/examination-schedule/"
                >
                  http://rcoi.mcko.ru/gia-9-oge-gve/examination-schedule/
                </a>{' '}
                <br />
                ГИА-11:{' '}
                <a
                  className="Link"
                  rel="noopener noreferrer"
                  target="_blank"
                  href="http://rcoi.mcko.ru/gia-11-ege-gve/exam-schedule/"
                >
                  http://rcoi.mcko.ru/gia-11-ege-gve/exam-schedule/
                </a>
              </p>
            </Message>
          </div>
        )}
      </div>

      <Button
        type="submit"
        primary
        progress={loadingExamData || undefined}
        onClick={buttonHandler}
      >
        Продолжить
      </Button>

      {subjectsInfo.length ? (
        <>
          <SubjectsInfo subjectsInfo={subjectsInfo} />

          {hasAvailableExams ? (
            <div className="StepGia__iAgree">
              <MosCheckbox
                checked={agreementOn}
                onChange={e => {
                  agree(e);
                  saveDraft();
                }}
              >
                Я соглашаюсь с тем, что в случае удовлетворения апелляции
                количество ранее выставленных баллов может измениться как в
                сторону увеличения, так и в сторону уменьшения количества баллов
              </MosCheckbox>
            </div>
          ) : null}
        </>
      ) : null}
    </form>
  );
};

StepGia.defaultProps = {
  subjectsInfo: [],
  appealResultNotReady: null,
  giaSelected: giaSelectedDefaultProp,
  agreementOn: false,
  invalidExamAttributes: false,
  registrationCodes: [],
  documentNumbers: []
};

StepGia.propTypes = {
  subjectsInfo: PropTypes.array,

  agreementOn: PropTypes.bool,
  invalidExamAttributes: PropTypes.bool,
  loadingExamData: PropTypes.bool.isRequired,
  hasAvailableExams: PropTypes.bool.isRequired,
  appealResultNotReady: PropTypes.bool,
  giaSelected: giaSelectedPropTypes,
  giaBlur: giaBlurPropTypes,
  registrationCodes: PropTypes.arrayOf(PropTypes.string),
  documentNumbers: PropTypes.arrayOf(PropTypes.string),

  agree: PropTypes.func.isRequired,
  getExamData: PropTypes.func.isRequired,
  changeGiaItem: PropTypes.func.isRequired,
  blurGiaItem: PropTypes.func.isRequired
};

export default compose(
  connect(structuredSelector, dispatch =>
    bindActionCreators(
      {
        changeGiaItem,
        blurGiaItem,
        getExamData,
        agree,
        saveDraft
      },
      dispatch
    )
  )
)(StepGia);
